import React, { useState, useCallback, useMemo, memo, MouseEvent, useContext } from 'react'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { Modal } from '@mui/material'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { EditFieldForm } from 'src/components/shared/edit-field-form/EditFieldForm'
import { getExpirationMinDate } from 'src/utils/expirationDateUtils'
import { useDispatch, useSelector } from 'react-redux'
import {
  DeclineOnboardingStep,
  FetchRejectionReasonsToDecline,
  RejectOnboarding,
  UpdateCollectData,
} from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { CollectedData } from 'src/interfaces/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'
import { GSIcon } from 'src/components/shared/GSIcon'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { useModalOpen } from 'src/hooks/useModalOpen'
import { MenuActionItem } from 'src/interfaces/ui'
import { colors } from 'src/theme'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { DeclineReasonModal } from './DeclineReasonModal'
import { getCollectDataStepTitle } from 'src/utils/collectedData'
import { RejectionReason } from 'src/interfaces/rejectionReason'
import { ContractorDetailsModalContext } from '../ContractorDetailsModal.context'
import { CollectedDataStepType } from 'src/constants/collectedData'
import { getUser } from 'src/selectors/user'

interface Props {
  collectedData: CollectedData
  contractorDetails: ContractorDetails
  showExpirationOption?: boolean
  showDeclineOption?: boolean
  onAction(): void | Promise<void>
}

const StepActionsMenuComponent = ({
  collectedData,
  contractorDetails,
  showDeclineOption,
  showExpirationOption,
  onAction,
}: Props) => {
  const dispatch = useDispatch()

  const user = useSelector(getUser)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isDeclineLoading, setIsDeclineLoading] = useState(false)

  const { triggerStatusDelayedContractorRefresh } = useContext(ContractorDetailsModalContext)

  const {
    isOpen: isExpirationModalOpen,
    open: openExpirationModal,
    close: closeExpirationModal,
  } = useModalOpen()
  const {
    isOpen: isDeclineReasonOpen,
    open: openDeclineReason,
    close: closeDeclineReason,
  } = useModalOpen()
  const {
    isOpen: isSensitiveModalOpen,
    open: openSensitiveModal,
    close: closeSensitiveModal,
  } = useModalOpen()

  const container = useMemo(() => document.getElementById('root'), [])

  const expirationMinDateOptions = useMemo(
    () => getExpirationMinDate(collectedData.effectiveAt),
    [collectedData.effectiveAt],
  )

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleSetExpirationDate = useCallback(
    async (expiresAt: string) => {
      try {
        await createAsyncAction(
          dispatch,
          UpdateCollectData.request({
            stepId: collectedData.collectDataStep.id,
            partnerDriverId: contractorDetails.id,
            expiresAt,
          }),
        )

        await onAction()

        closeExpirationModal()

        showToast('Expiration date has been saved.')
      } catch (err) {
        handleApiError(err)
      }
    },
    [collectedData, contractorDetails, onAction, dispatch, closeExpirationModal],
  )

  const rejectContractor = useCallback(async () => {
    try {
      triggerStatusDelayedContractorRefresh()

      await createAsyncAction(
        dispatch,
        RejectOnboarding.request({ partnerDriverId: contractorDetails.id }),
      )

      await onAction()

      showToast('Step is rejected.')
    } catch (err) {
      handleApiError(err)
    }
  }, [dispatch, contractorDetails, onAction])

  const handleDecline = useCallback(async () => {
    if (collectedData.collectDataStep.type === CollectedDataStepType.InPersonMeeting) {
      rejectContractor()
    } else {
      try {
        const response = await createAsyncAction<RejectionReason[], void>(
          dispatch,
          FetchRejectionReasonsToDecline.request(),
        )

        if (response && response.length > 0) {
          openDeclineReason()
        } else {
          openSensitiveModal()
        }
      } catch (err) {
        handleApiError(err)
        openSensitiveModal()
      }
    }
  }, [collectedData, rejectContractor, dispatch, openDeclineReason, openSensitiveModal])

  const handleDeclineStep = useCallback(
    async (reason: RejectionReason | null, onComplete: () => void) => {
      setIsDeclineLoading(true)

      try {
        await createAsyncAction(
          dispatch,
          DeclineOnboardingStep.request({
            stepId: collectedData.collectDataStep.id,
            partnerDriverId: contractorDetails.id,
            reviewedByEmail: user?.email || '',
            partnerReviewRejectionReasonId: reason?.id,
            declineReason: reason?.name,
          }),
        )

        await onAction()

        showToast('Step is sent to recollection.')
      } catch (err) {
        handleApiError(err)
      }

      setIsDeclineLoading(false)

      onComplete()
    },
    [collectedData, contractorDetails, user?.email, dispatch, onAction],
  )

  const handleDeclineReasonConfirm = useCallback(
    async (reason: RejectionReason) => {
      await handleDeclineStep(reason, closeDeclineReason)
    },
    [handleDeclineStep, closeDeclineReason],
  )

  const handleSensitiveModalConfirm = useCallback(async () => {
    await handleDeclineStep(null, closeSensitiveModal)
  }, [handleDeclineStep, closeSensitiveModal])

  const actionItems: Array<MenuActionItem> = useMemo(() => {
    const res = []

    if (showExpirationOption) {
      res.push({
        label: 'Set expiration date',
        icon: <GSIcon iconName="gs_calendar_edit" width={20} />,
        onClick: () => {
          openExpirationModal()
          setAnchorEl(null)
        },
      })
    }

    if (showDeclineOption) {
      res.push({
        label: 'Decline',
        icon: <GSIcon iconName="gs_icon_thumbs_down" width={20} />,
        color: colors.COLORS_RED_500,
        onClick: () => {
          handleDecline()
          setAnchorEl(null)
        },
      })
    }

    return res
  }, [showDeclineOption, showExpirationOption, handleDecline])

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl])

  return (
    <>
      <Button
        color="grey"
        size="small"
        leftIcon={<GSIcon iconName="gs_dot_3" css={tw`self-center`} width={20} height={20} />}
        onClick={handleClick}
      />
      <ActionMenu
        anchorEl={anchorEl}
        items={actionItems}
        isOpen={isMenuOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        itemClassName={tw`w-[187px]`}
        onClose={handleCloseMenu}
      />
      <Modal
        container={container}
        open={isExpirationModalOpen}
        onClose={closeExpirationModal}
        aria-labelledby="set-expiration-title"
        css={tw`flex justify-center items-center backdrop-blur`}
      >
        <div css={tw`bg-white rounded-lg`}>
          <ModalHeader title="Set expiration date" onClose={closeExpirationModal} />
          <EditFieldForm
            label="Expiration date"
            editFiledType="date"
            minDate={expirationMinDateOptions.minDate}
            minDateError={expirationMinDateOptions.minDateError}
            initialValue={collectedData.expiresAt}
            onSubmit={handleSetExpirationDate}
            onClose={closeExpirationModal}
          />
        </div>
      </Modal>
      <DeclineReasonModal
        isOpen={isDeclineReasonOpen}
        onClose={closeDeclineReason}
        onConfirm={handleDeclineReasonConfirm}
        isLoading={isDeclineLoading}
      />
      <SensitiveActionModal
        isOpen={isSensitiveModalOpen}
        title="Recollect step?"
        text={`Are you sure you want to initiate ${getCollectDataStepTitle(
          collectedData,
        )} recollection?`}
        submitLabel="Yes"
        onClose={closeSensitiveModal}
        onSubmit={handleSensitiveModalConfirm}
      />
    </>
  )
}

export const StepActionsMenu = memo(StepActionsMenuComponent)
